import { useCallback, useState } from 'react';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import {
  RequestCareProgramResponse,
  useRequestCareProgramMutation,
} from 'services/careProgramRequestService';
import { isFetchBaseQueryError } from 'utils';

interface RequestCareProgramParams {
  programId: number;
}

interface UseCareProgramEnrollmentResult {
  requestEnrollment: (
    params: RequestCareProgramParams,
  ) => Promise<RequestCareProgramResponse | undefined>;
  isSubmitting: boolean;
  isSuccess: boolean;
  data: RequestCareProgramResponse | undefined;
  error: string | null;
}

/** Submits a care program enrollment request; isSubmitting is true immediately on call. */
export const useCareProgramEnrollment = (): UseCareProgramEnrollmentResult => {
  const { translation } = useTranslationContext();
  const user = useLoggedInUser();
  const [requestCareProgram, mutationState] = useRequestCareProgramMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const requestEnrollment = useCallback(
    async ({
      programId,
    }: RequestCareProgramParams): Promise<RequestCareProgramResponse | undefined> => {
      setSubmissionError(null);

      try {
        if (!user?.uuid) {
          throw new Error('No logged in user for enrollment request');
        }
        const request = {
          memberUuid: user.uuid,
          programId,
        };

        return await requestCareProgram(request).unwrap();
      } catch (error) {
        const isPendingRequestError = isFetchBaseQueryError(error) && error.status === 412;
        if (!isPendingRequestError) {
          setSubmissionError(translation('request_submission_error_alert_message'));
        }
        throw error;
      }
    },
    [user?.uuid, requestCareProgram, translation],
  );

  return {
    requestEnrollment,
    isSubmitting: mutationState.isLoading,
    isSuccess: mutationState.isSuccess,
    data: mutationState.data,
    error:
      submissionError ??
      (mutationState.isError ? translation('request_submission_error_alert_message') : null),
  };
};
